import React, { useState, useEffect } from "react";
import { skillsFaltantes, cursosDeSkills, cursoConcluido, instituicoesConhecidas } from "../services/api";

/**
 * "MELHORAR MINHAS SKILLS" — o que falta no currículo, quanto cada skill vale, e onde aprender.
 *
 * Ideia do Nelson (25/09/2026), refinada em quatro passos na conversa:
 *   1. "e se buscássemos cursos online, mesmo sendo pagos, e criássemos uma rota da skill para os cursos"
 *   2. "não seria melhor um botão melhorar minhas skills faltantes e abre um popup?"  ← em vez de balão por chip
 *   3. "esse popup abre as skills com um + e separa por gratuitos e pagos e outro + com os cursos por portal"
 *   4. "ter a opção de fiz o curso, e abre para ele adicionar o certificado"
 *
 * ⚖️ O NÚMERO É SOBRE O PERFIL DELA. "Excel é pedida em 2.101 vagas do Brasil" é curiosidade; "+71 vagas
 * SUAS" é motivo para fazer o curso. O backend calcula dentro do recorte de área e cidade do currículo.
 *
 * 📚 GRATUITO PRIMEIRO, e não por escrúpulo: as skills mais pedidas do mural são soft, e todas têm curso
 * público de graça (CATE, Escola Virtual.Gov, Fundação Bradesco, Sebrae, Microsoft Learn).
 *
 * 💸 SEM LINK DE AFILIADO, e o rodapé diz isso. Enquanto não houver parceria assinada, o eaiRH não ganha
 * nada com a indicação — e afirmar isso é o que separa serviço ao candidato de empurrar curso para quem
 * está desempregado.
 */
/**
 * `skillsDaVaga` muda a PERGUNTA do popup (26/09/2026, defeito que o Nelson pegou com print):
 *
 *   sem ela  → "o que estudar para abrir mais vagas no seu perfil" (tela do currículo)
 *   com ela  → "o que falta para ESTA vaga"                        (tela da vaga)
 *
 * Antes o popup só sabia a primeira pergunta. Aberto na vaga, ele listava CLT, Logística e Power BI como
 * faltantes na tela e, no popup, oferecia curso de "Integração de sistemas" e "PL/SQL" — skills de outras
 * vagas do recorte. *"Nenhuma delas aparece no form"*, e ele estava certo.
 */
export default function MelhorarSkills({ aberto, onFechar, aoAdicionar, skillsDaVaga }) {
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState(null);
  const [abertaSkill, setAbertaSkill] = useState(0);
  const [abertoPortal, setAbertoPortal] = useState({});
  const [cert, setCert] = useState(null);      // { skill, curso, onde } quando o "já fiz" abre
  const [salvo, setSalvo] = useState(null);

  useEffect(() => {
    if (!aberto) return;
    setDados(null); setErro(null);
    const daVaga = (skillsDaVaga || []).filter(Boolean);
    const busca = daVaga.length > 0 ? cursosDeSkills(daVaga) : skillsFaltantes();
    busca.then(setDados).catch((e) => setErro(e.message));
  }, [aberto, skillsDaVaga]);

  if (!aberto) return null;

  const fechar = () => { setCert(null); setSalvo(null); onFechar && onFechar(); };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-4"
         onClick={(e) => { if (e.target === e.currentTarget) fechar(); }}>
      <div className="w-full max-w-xl max-h-[86vh] overflow-auto rounded-2xl bg-white shadow-2xl">
        <div className="sticky top-0 rounded-t-2xl border-b bg-white px-5 pb-3 pt-4">
          <button onClick={fechar} aria-label="Fechar" className="float-right text-2xl leading-none text-gray-400 hover:text-gray-700">×</button>
          <h3 className="text-lg font-bold text-primary">Melhorar minhas skills</h3>
          {dados?.daVaga && (
            <p className="mt-1 text-sm text-gray-600">
              {dados.itens?.length > 0
                ? <>Estas são as skills que <b>esta vaga pede</b> e ainda não estão no seu currículo — e onde aprender cada uma:</>
                : <>Seu currículo já cobre o que esta vaga pede. 👏</>}
            </p>
          )}
          {dados?.temRecorte && dados.vagasDoPerfil > 0 && (
            <p className="mt-1 text-sm text-gray-600">
              Comparamos seu currículo com as <b>{dados.vagasDoPerfil}</b> vagas
              {dados.area ? <> de <b>{dados.area}</b></> : null}
              {dados.cidade ? <> em <b>{dados.cidade}</b></> : null}.
              {dados.itens?.length > 0 && " Estas são as skills que mais abririam vagas para você:"}
            </p>
          )}
        </div>

        {erro && <p className="px-5 py-6 text-sm text-red-600">{erro}</p>}
        {!dados && !erro && <p className="px-5 py-8 text-sm text-gray-500">Calculando…</p>}

        {/* Sem área no perfil não há recorte — e sem recorte o número não significa "suas vagas". */}
        {dados && !dados.daVaga && !dados.temRecorte && (
          <p className="px-5 py-6 text-sm text-gray-600">
            Preencha a <b>área de atuação</b> no seu currículo para eu calcular quais skills abririam mais vagas para você.
          </p>
        )}
        {dados?.temRecorte && dados.itens?.length === 0 && (
          <p className="px-5 py-6 text-sm text-gray-600">
            Seu currículo já cobre as skills mais pedidas nas vagas do seu perfil. 👏
          </p>
        )}

        {dados?.itens?.map((it, i) => {
          const gratuitos = it.cursos.filter((c) => c.gratuito);
          const pagos = it.cursos.filter((c) => !c.gratuito);
          const aberta = abertaSkill === i;
          return (
            <div key={it.skill} className="border-b">
              <button onClick={() => setAbertaSkill(aberta ? -1 : i)}
                      className="flex w-full flex-col items-start gap-1 px-5 py-3.5 text-left hover:bg-gray-50">
                <span className="flex items-center gap-2 text-[15px]">
                  <span className="inline-flex h-[18px] w-[18px] items-center justify-center rounded bg-orange-50 font-bold text-orange-700">
                    {aberta ? "−" : "+"}
                  </span>
                  <b>{it.skill}</b>
                </span>
                {/* A barra de "+N vagas" é do modo recorte. No modo vaga não existe N: é UMA vaga, e
                    inventar um número ali seria mentir sobre o que a skill abre. */}
                {!dados.daVaga && (
                  <>
                    <span className="ml-[26px] text-[12.5px] text-gray-600">
                      <span className="mr-1.5 rounded bg-green-50 px-1.5 py-0.5 font-bold text-green-800">+{it.vagasAMais} vagas</span>
                      passariam a dar match — <b>{it.percentual}%</b> das {dados.vagasDoPerfil} do seu perfil pedem esta skill
                    </span>
                    <span className="ml-[26px] mt-1 block h-[5px] w-[230px] max-w-full overflow-hidden rounded-full bg-gray-100">
                      <i className="block h-full rounded-full bg-orange-500" style={{ width: `${Math.min(it.percentual, 100)}%` }} />
                    </span>
                  </>
                )}
              </button>

              {aberta && (
                <div className="px-5 pb-4">
                  {[["Gratuitos", gratuitos, true], ["Pagos", pagos, false]].map(([rotulo, lista, gratis]) =>
                    lista.length === 0 ? null : (
                      <div key={rotulo} className="mb-2.5 overflow-hidden rounded-xl border">
                        <div className={`px-3 py-2 text-[12.5px] font-bold ${gratis ? "bg-green-50 text-green-800" : "bg-orange-50 text-orange-800"}`}>
                          {rotulo} <span className="ml-1 rounded-full bg-black/10 px-2 text-[11px]">{lista.length}</span>
                        </div>
                        {/* agrupado por PORTAL, com + — pedido do Nelson: lista corrida vira parede de texto */}
                        {Object.entries(lista.reduce((acc, c) => {
                          (acc[c.onde] = acc[c.onde] || []).push(c); return acc;
                        }, {})).map(([onde, cursos]) => {
                          const k = `${i}|${rotulo}|${onde}`;
                          const on = !!abertoPortal[k];
                          return (
                            <div key={onde} className="border-t">
                              <button onClick={() => setAbertoPortal({ ...abertoPortal, [k]: !on })}
                                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13.5px] hover:bg-gray-50">
                                <span className="inline-flex h-[18px] w-[18px] items-center justify-center rounded bg-orange-50 font-bold text-orange-700">
                                  {on ? "−" : "+"}
                                </span>
                                {onde} <span className="rounded-full bg-black/10 px-2 text-[11px]">{cursos.length}</span>
                              </button>
                              {on && (
                                <div className="pb-2.5 pl-9 pr-3">
                                  {cursos.map((c) => (
                                    <div key={c.url + c.titulo} className="flex items-center justify-between gap-2 border-t border-dashed py-2 first:border-t-0">
                                      <a href={`${c.url}${c.url.includes("?") ? "&" : "?"}de=curso-${encodeURIComponent(it.skill)}`}
                                         target="_blank" rel="noopener noreferrer"
                                         className="text-[13px] text-primary hover:underline">{c.titulo}</a>
                                      <button onClick={() => setCert({
                                        skill: it.skill, onde: c.onde,
                                        // 🚫 LINK DE BUSCA NÃO TEM NOME DE CURSO. O título dele é
                                        // "Buscar X em Y" — gravar isso no currículo seria escrever
                                        // bobagem no documento que a pessoa manda para a empresa.
                                        // Nesse caso o campo abre VAZIO e ela informa o que fez.
                                        curso: c.origem === "busca" ? "" : c.titulo,
                                        precisaNome: c.origem === "busca",
                                      })}
                                              className="whitespace-nowrap rounded-full border border-primary px-2.5 py-0.5 text-[11.5px] font-bold text-primary hover:bg-primary hover:text-white">
                                        Já fiz
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ))}
                </div>
              )}
            </div>
          );
        })}

        {dados?.itens?.length > 0 && (
          <>
            <div className="mx-5 mt-3.5 rounded-xl border border-blue-200 bg-blue-50 px-3.5 py-3 text-[13px] text-blue-900">
              <b>Terminou um curso?</b> Clique em “Já fiz” — a skill entra no seu currículo e o match é refeito.
            </div>
            <p className="px-5 pb-4 pt-2.5 text-[11.5px] text-gray-500">
              O eaiRH não ganha nada com estas indicações. Cursos gratuitos aparecem primeiro.
            </p>
          </>
        )}
      </div>

      {cert && (
        <ModalCertificado
          dados={cert}
          onFechar={() => setCert(null)}
          onSalvo={(r) => { setCert(null); setSalvo(r); aoAdicionar && aoAdicionar(r); }}
        />
      )}
      {salvo && <ModalSalvo dados={salvo} onFechar={() => { setSalvo(null); fechar(); }} />}
    </div>
  );
}

/**
 * "JÁ FIZ ESTE CURSO" — a skill vai para o match, o curso vai para os certificados.
 *
 * 📎 O comprovante é OPCIONAL de propósito: exigir anexo faz desistir quem fez o curso e não tem o PDF
 * à mão (curso gratuito muitas vezes nem emite), e aí se perde o que importa — a skill no match. Quem
 * anexa ganha o selo "com comprovante"; quem não anexa entra do mesmo jeito.
 */
function ModalCertificado({ dados, onFechar, onSalvo }) {
  const [curso, setCurso] = useState(dados.curso || "");
  // Quando veio de um link de busca, a gente não sabe qual curso ela fez — e não dá para adivinhar.
  const precisaNome = !!dados.precisaNome;
  const [emissor, setEmissor] = useState((dados.onde || "").replace(/\s+/g, " ").trim());
  const [ano, setAno] = useState(String(new Date().getFullYear()));
  const [anexo, setAnexo] = useState(null);
  // Sugestões de instituição: o catálogo + o que outros candidatos já digitaram. Evita a mesma escola
  // entrar três vezes com grafias diferentes — pedido do Nelson (25/09).
  const [instituicoes, setInstituicoes] = useState([]);
  useEffect(() => { instituicoesConhecidas().then(setInstituicoes).catch(() => {}); }, []);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState(null);

  const salvar = async () => {
    setSalvando(true); setErro(null);
    try {
      const r = await cursoConcluido({ skill: dados.skill, curso, emissor, ano });
      onSalvo({ ...r, curso, emissor, ano, comAnexo: !!anexo });
    } catch (e) { setErro(e.message); setSalvando(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/55 p-4"
         onClick={(e) => { if (e.target === e.currentTarget) onFechar(); }}>
      <div className="w-full max-w-md overflow-auto rounded-2xl bg-white shadow-2xl">
        <div className="border-b px-5 pb-3 pt-4">
          <button onClick={onFechar} aria-label="Fechar" className="float-right text-2xl leading-none text-gray-400 hover:text-gray-700">×</button>
          <h3 className="text-[17px] font-bold text-primary">Você concluiu este curso?</h3>
        </div>

        <div className="space-y-2.5 px-5 py-4">
          <div className="rounded-xl border p-3.5">
            <div className="mb-1.5 text-[12.5px] font-bold text-primary">
              Entra nas suas skills <span className="ml-1 rounded bg-green-50 px-1.5 py-0.5 text-[10.5px] text-green-800">automático</span>
            </div>
            <span className="inline-block rounded-full bg-blue-50 px-3 py-1 text-[13px] font-bold text-primary">{dados.skill}</span>
            <span className="ml-1.5 text-xs text-gray-500">é o que o match lê para te achar vagas</span>
          </div>

          <div className="rounded-xl border p-3.5">
            <div className="mb-1 text-[12.5px] font-bold text-primary">
              Entra nos seus certificados <span className="ml-1 rounded bg-orange-50 px-1.5 py-0.5 text-[10.5px] text-orange-800">confira antes</span>
            </div>
            <label className="mt-1.5 block text-[11.5px] text-gray-500">
              Curso {precisaNome && <span className="text-orange-700">— qual você fez?</span>}
            </label>
            <input value={curso} onChange={(e) => setCurso(e.target.value)}
                   placeholder={precisaNome ? "Digite o nome do curso que você concluiu" : ""}
                   className="w-full rounded-lg border px-2.5 py-1.5 text-[13px]" />
            {precisaNome && curso.trim().length === 0 && (
              <p className="mt-1 text-[11.5px] text-orange-700">
                Você veio de uma busca, então não sabemos qual curso foi. Sem o nome, a gente adiciona só a skill.
              </p>
            )}
            {/* Certificado sem instituição não diz nada a quem contrata: os dois campos andam juntos.
                No fallback a instituição já vem da plataforma onde ela clicou — mas fica editável,
                porque ela pode ter clicado na busca da Udemy e feito o curso em outro lugar. */}
            {curso.trim().length > 0 && emissor.trim().length === 0 && (
              <p className="mt-1 text-[11.5px] text-orange-700">
                Informe também a instituição — certificado sem ela não diz muito a quem contrata.
              </p>
            )}
            <div className="grid grid-cols-[1fr_90px] gap-2">
              <div>
                <label className="mt-1.5 block text-[11.5px] text-gray-500">Instituição</label>
                <input value={emissor} onChange={(e) => setEmissor(e.target.value)} list="dl-instituicoes"
                       placeholder="Escolha ou digite" className="w-full rounded-lg border px-2.5 py-1.5 text-[13px]" />
                <datalist id="dl-instituicoes">
                  {instituicoes.map((x) => <option key={x} value={x} />)}
                </datalist>
              </div>
              <div>
                <label className="mt-1.5 block text-[11.5px] text-gray-500">Ano</label>
                <input value={ano} onChange={(e) => setAno(e.target.value)} className="w-full rounded-lg border px-2.5 py-1.5 text-[13px]" />
              </div>
            </div>
            <p className="mt-1.5 text-xs text-gray-500">é o que o recrutador vê no seu currículo</p>
          </div>

          <div className="rounded-xl border border-dashed p-3.5">
            <div className="mb-1.5 text-[12.5px] font-bold text-gray-600">
              Comprovante <span className="ml-1 rounded bg-gray-100 px-1.5 py-0.5 text-[10.5px]">opcional</span>
            </div>
            <label className={`block cursor-pointer rounded-xl border-2 border-dashed p-4 text-center text-[13px] ${anexo ? "border-green-600 text-green-700" : "text-gray-500 hover:border-primary hover:text-primary"}`}>
              {anexo ? `📎 ${anexo.name}` : "Clique para anexar o certificado (PDF ou imagem)"}
              <input type="file" accept=".pdf,image/*" className="hidden" onChange={(e) => setAnexo(e.target.files?.[0] || null)} />
            </label>
            <p className="mt-2 text-xs text-gray-500">
              Sem anexo o curso entra assim mesmo — a skill vale no match do mesmo jeito.
              Com anexo, seu currículo mostra <b>📎 com comprovante</b>.
            </p>
          </div>

          <p className="text-xs text-gray-500">
            O eaiRH não valida o certificado: ele entra como declarado por você, igual ao resto do currículo.
          </p>
          {erro && <p className="text-sm text-red-600">{erro}</p>}
        </div>

        <div className="flex justify-end gap-2 px-5 pb-4">
          <button onClick={onFechar} className="rounded-full border px-4 py-1.5 text-[13px] font-semibold text-gray-600">Cancelar</button>
          <button onClick={salvar} disabled={salvando || (curso.trim().length > 0 && emissor.trim().length === 0)}
                  className="rounded-lg bg-primary px-4 py-2 text-[13.5px] font-bold text-white disabled:opacity-60">
            {salvando ? "Salvando…"
              : curso.trim().length === 0 ? "Adicionar só a skill"
              : emissor.trim().length === 0 ? "Informe a instituição"
              : "Adicionar ao meu currículo"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ModalSalvo({ dados, onFechar }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/55 p-4"
         onClick={(e) => { if (e.target === e.currentTarget) onFechar(); }}>
      <div className="w-full max-w-md rounded-2xl bg-white p-6 text-center shadow-2xl">
        <div className="text-4xl">✅</div>
        <h4 className="mb-1.5 mt-2 text-base font-bold text-green-800">Adicionado ao seu currículo</h4>
        <p className="text-[13.5px] text-gray-600">
          <b>Skill:</b> {dados.skill}
          {dados.curso && <><br /><b>Certificado:</b> {dados.curso}{dados.emissor ? ` (${dados.emissor}${dados.ano ? `, ${dados.ano}` : ""})` : ""}
            {dados.comAnexo && <span className="ml-1.5 rounded bg-green-50 px-1.5 py-0.5 text-[11px] font-bold text-green-800">📎 com comprovante</span>}</>}
        </p>
        <p className="mb-4 mt-2 text-[13px] text-gray-600">
          Seu match foi refeito — vagas que pediam esta skill passam a aparecer para você.
        </p>
        <button onClick={onFechar} className="rounded-lg bg-primary px-5 py-2 text-[13.5px] font-bold text-white">Fechar</button>
      </div>
    </div>
  );
}
